import React, { useState } from "react";
import PropTypes from "prop-types";
import { PageHeader, InlineWaiting } from "components/ui";
import { connect } from "react-redux";
import { find, pathOr } from "ramda";
import { getDocumentCategory } from "components/dd_newModules/Redux/Actions/documentCategory";
import { errorMessage } from "actions/message";
import { useFetch } from "hooks";
import { post } from "utils/api";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_ACCEPT,
  fileTypeNotAllowedMessage,
  isAllowedUploadFile
} from "utils/files";

const UploadPatientDocument = ({
  patientId,
  errorMessage,
  // uploadDocument,
  getDocumentCategory,
  closeModal,
  documentCategoryOptions
}) => {
  const uploadDocumentApi = useFetch({
    apiFn: ({ patientId, formData }) =>
      post(`/dme_portal/patients/${patientId}/documents`, formData),
    throwOnError: true
  });
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setDocument] = useState("");
  const [category, setDocumentCategory] = useState("");
  const [isMediaType, setIsMediaType] = useState(false);
  React.useEffect(() => {
    getDocumentCategory();
  }, []);

  const acceptedTypes = isMediaType ? IMAGE_UPLOAD_ACCEPT : DOCUMENT_UPLOAD_ACCEPT;

  const setSelectedDocument = event => {
    const MBSize = event.target.files[0].size / 1024;
    if (MBSize > 101376) {
      setDocument("");
      errorMessage("File size must be less than 99MB.");
    } else if (!isAllowedUploadFile(event.target.files[0].name, acceptedTypes)) {
      // S3DEV2-7865: the picker's accept list is only a hint, so tell the user right away.
      setDocument("");
      event.target.value = "";
      errorMessage(fileTypeNotAllowedMessage(acceptedTypes));
    } else {
      setDocument(event.target.files[0]);
    }
  };

  return (
    <div>
      <PageHeader title="Upload Document" />
      <div style={{ padding: 5 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridGap: 10,
            alignItems: "center"
          }}
        >
          <div>
            <label>
              Select Document Category
              <select
                value={category}
                onChange={({ target }) => {
                  const documentCategory = find(
                    value => target.value === value,
                    documentCategoryOptions
                  );
                  setDocumentCategory(target.value);
                  setIsMediaType(documentCategory && documentCategory.is_media);
                }}
              >
                <option />
                {documentCategoryOptions.map(({ text, value }) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Enter Document Title
            <input
              maxLength={90}
              type="text"
              value={title}
              onChange={({ target }) => setTitle(target.value)}
            />
          </label>
        </div>
        <div>
          <label>
            Enter Document Description
            <textarea
              rows={3}
              maxLength={390}
              id="description"
              onChange={({ target: { value } }) => setDescription(value)}
              value={description}
              placeholder="Enter Document Description"
            />
          </label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridGap: 10,
              alignItems: "center"
            }}
          >
            <div>
              <input
                accept={acceptedTypes}
                onChange={setSelectedDocument}
                id="file"
                type="file"
              />
              <small>
                <label style={{ color: "red" }}>
                  File size should be less than 99MB
                </label>
              </small>
            </div>
            {uploadDocumentApi.isLoading ? (
              <InlineWaiting />
            ) : (
              <button
                onClick={async () => {
                  // The category can change after the file was picked (media = images only).
                  if (!isAllowedUploadFile(file.name, acceptedTypes)) {
                    errorMessage(fileTypeNotAllowedMessage(acceptedTypes));
                    return;
                  }
                  let formData = new FormData();
                  formData.append("active", true);
                  formData.append("title", title);
                  formData.append("document_category_id", category);
                  formData.append("description", description);
                  formData.append("file", file);
                  try {
                    await uploadDocumentApi.fetchData({ formData, patientId });
                  } catch (error) {
                    // S3DEV2-7865: show the API's reason (for example a rejected file type)
                    // and keep the modal open so the user can pick another file.
                    errorMessage(
                      pathOr("Upload failed.", ["response", "body", "message"], error)
                    );
                    return;
                  }
                  closeModal();
                }}
                disabled={
                  category.trim().length === 0 ||
                  title.trim().length === 0 ||
                  description.trim().length === 0 ||
                  file.length === 0
                }
              >
                Upload
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

UploadPatientDocument.propTypes = {
  patientId: PropTypes.string.isRequired,
  uploadDocument: PropTypes.func.isRequired,
  documentCategoryOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  getDocumentCategory: PropTypes.func.isRequired,
  errorMessage: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired
};

export default connect(
  state => ({
    documentCategoryOptions: pathOr(
      [],
      ["rdc_documentCategory", "DocumentCategories"],
      state.getIn(["newRootReducer"])
    )
      .filter(({ active }) => active)
      .map(data => ({
        text: data.document_category_name,
        value: data.document_category_id,
        is_media: data.is_media
      }))
  }),
  { getDocumentCategory, errorMessage }
)(UploadPatientDocument);
